import type { KeyboardEvent } from 'react';
import type { QuestionChoiceType } from '~/model/QuestionChoiceType';
import { VpIcon, VpIconButton } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { ResizedImg } from '~/components/commons/ResizedImg/ResizedImg';
import { keyboardKeys } from '~/const/keyCodes';
import { ChoicePictureFallback } from './ChoicePictureFallback';

enum ChoicePictureFormat {
  SMALL = '220x0',
  LARGE = '356x0',
}

export const ChoicePicture = ({
  choice,
  openGallery,
  isChecked = false,
  handleClick,
}: {
  choice: QuestionChoiceType;
  openGallery: (url: string) => void;
  isChecked: boolean;
  handleClick?: () => void;
}) => {
  const t = useTranslations();

  const handleKeyDown = (e: KeyboardEvent<HTMLLabelElement>) =>
    handleClick && e.key === keyboardKeys.ENTER && handleClick();

  return (
    <>
      {choice.media
        ? (
            <span className="control-image">
              <label
                data-testid="choice-picture-label"
                htmlFor={`choice-${choice.id}`}
                aria-labelledby={`label-choice-${choice.id}`}
                aria-disabled="true"
                tabIndex={-1}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
              >
                <ResizedImg
                  data-testid="choice-picture"
                  src={choice.media.content_url}
                  format={`${ChoicePictureFormat.LARGE}`}
                  fill
                  priority
                  sizes="356px"
                  style={{ objectFit: 'contain' }}
                  alt=""
                />
              </label>
              <span className="zoom-button">
                <VpIconButton
                  data-testid="picture-zoom"
                  size="small"
                  tabIndex={-1}
                  title={t('GLOBAL.PICTURE_ZOOM')}
                  variant="secondary"
                  onClick={() => openGallery(
                    getResizedPictureUrl(choice.media.content_url, '1600x0'),
                  )}
                >
                  <VpIcon name="eye" />
                </VpIconButton>
              </span>
            </span>
          )
        : (
            <ChoicePictureFallback
              handleClick={handleClick}
              handleKeyDown={handleKeyDown}
              choice={choice}
            />
          )}
      <span className="control-outline" />
      {isChecked && (
        <span className="control-checked-icon">
          <VpIcon name="check-circle" />
        </span>
      )}
    </>
  );
};
